import { PrismicRichText } from '@prismicio/react';
import { GetStaticPropsResult, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { RichText } from 'prismic-reactjs';
import { CareerSlugProps, Locale, Params } from '../../types';
import useUpdatePreviewRef from '../../utils/hooks/useUpdatePreviewRef';

import { filter } from '@prismicio/client';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { i18nConfig } from '../../next-i18next.config';
import { Client } from '../../prismic-configuration';
import { getJobs } from '../../utils/prismic/jobs';
import getPrismicMenuByLocale from '../../utils/prismic/menu';
import getPrismicOptionsByLocale from '../../utils/prismic/options';
import { ApplicationForm, Layout } from '../../components';
import { REVALIDATION_TIME } from '../../utils/constants';

const Job: NextPage<CareerSlugProps> = ({
  options,
  job,
  menu,
  preview,
  slug,
}) => {
  useUpdatePreviewRef(preview, job.id);
  const { tags } = job;

  return (
    <>
      <NextSeo
        title={`Prickle | Careers | ${RichText.asText(job.data.title)}`}
        description={`Prickle | Careers | ${RichText.asText(job.data.title)}`}
      />
      <Layout options={options} menu={menu}>
        {job.data && (
          <>
            <section className="w-full h-full p-6 text-white bg-dark">
              <div className="container mx-auto">
                {job.data.title && (
                  <div
                    data-testid="job-detail-title"
                    className="my-16 text-5xl text-center text-white font-body"
                  >
                    {RichText.asText(job.data.title)}
                  </div>
                )}
                {job.data.description && (
                  <div className="max-w-3xl m-auto mt-16 mb-32 prose text-white">
                    <PrismicRichText
                      field={job.data.description}
                      fallback={<p>No content</p>}
                      components={{
                        heading1: ({ children, key }) => (
                          <h1
                            className="my-5 text-2xl text-white font-body"
                            key={key}
                          >
                            {children}
                          </h1>
                        ),
                        heading2: ({ children, key }) => (
                          <h2
                            className="my-4 text-xl text-white font-body"
                            key={key}
                          >
                            {children}
                          </h2>
                        ),
                        list: ({ children, key }) => (
                          <ul className="mb-5 ml-8 list-disc" key={key}>
                            {children}
                          </ul>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>
              <section className="max-w-3xl mx-auto my-24 text-black">
                <div className="container px-6 mx-auto md:px-0 ">
                  <ApplicationForm
                    role={slug}
                    slug={slug}
                    tag={tags[0]}
                    endpoint="/api/application"
                  ></ApplicationForm>
                </div>
              </section>
            </section>

            <style jsx>
              {`
                ::-webkit-file-upload-button {
                  display: none;
                }
              `}
            </style>
          </>
        )}
      </Layout>
    </>
  );
};

export const getStaticPaths = async ({ locales }: { locales: string[] }) => {
  const jobs = await getJobs();
  const paths: { params: { slug: string | null }; locale: string }[] = [];
  jobs.results.forEach((job: any) => {
    locales.forEach((locale: string) => {
      paths.push({
        params: { slug: job.uid },
        locale,
      });
    });
  });
  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  params,
  preview = false,
  previewData,
  locale,
}: {
  params: Params;
  preview: boolean;
  previewData: any;
  locale: Locale;
  defaultLocale: string;
}): Promise<GetStaticPropsResult<CareerSlugProps>> => {
  const options = await getPrismicOptionsByLocale(locale, previewData);
  const menu = await getPrismicMenuByLocale(locale, previewData);
  let job;
  try {
    const client = Client({ previewData });
    job = await client.getByUID('job', params.slug, {
      lang: locale,
      predicates: [filter.at('my.job.active', true)],
    });
  } catch (error: any) {
    if (
      error.message === 'No documents were returned' ||
      error === 'No documents were returned'
    ) {
      return {
        notFound: true,
      };
    }
    throw new Error(error);
  }
  if (!job) {
    return {
      notFound: true,
    };
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'form'], i18nConfig)),
      job,
      options,
      menu,
      preview,
      slug: params.slug,
    },
    revalidate: REVALIDATION_TIME,
  };
};

export default Job;
